/**
 * @fileoverview The instructor page's "Notes owed" card (C4, decided Nov 1,
 * 2026): the class meetings this instructor owes notes for over the
 * dashboard's window, and Chase notes, which asks them, and only them, in
 * their updates in Hans. Their co-teachers are not asked from here; a class
 * page's Chase notes still asks everyone teaching it.
 *
 * Read on its own beside the page (GET /org/instructors/{id}/notes-owed), so
 * the page never waits on it. Nothing owed, or a read that failed, draws
 * nothing: the card is there to act on, not to report an absence
 * (mobile/src/components/org/NotesOwedCard.js).
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { dayLabel } from "@/lib/orgReporting";
import { Button, Card } from "@/components/studio/primitives";

/** The meetings listed before "and N more". */
const SHOWN = 5;

export default function NotesOwedCard({ userId, name }) {
  const [owed, setOwed] = useState(null);
  const [chasing, setChasing] = useState(false);

  useEffect(() => {
    let current = true;
    orgApi
      .get(`/org/instructors/${userId}/notes-owed`)
      .then((res) => current && setOwed(res.data || null))
      .catch(() => current && setOwed(null));
    return () => {
      current = false;
    };
  }, [userId]);

  const chase = async () => {
    if (chasing) return;
    setChasing(true);
    try {
      await orgApi.post(`/org/instructors/${userId}/notes-chase`, {});
      toast.success(`Asked ${name} for notes`, { description: "It lands in their updates in Hans. No email is sent." });
    } catch (err) {
      if (err?.response?.status === 409) toast(getOrgErrorMessage(err, "They don't owe any notes."));
      else toast.error(getOrgErrorMessage(err, "Couldn't send the reminder"));
    } finally {
      setChasing(false);
    }
  };

  const sessions = owed?.sessions || [];
  if (!sessions.length) return null;
  const more = sessions.length - SHOWN;
  return (
    <Card padding={0} className="mb-6 overflow-hidden" data-testid="instructor-notes-owed">
      <div className="flex items-baseline justify-between gap-3 px-5 pb-3 pt-5">
        <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Notes owed</h2>
        <span className="font-sans text-[13px] text-[var(--text-tertiary)]">
          {sessions.length} class meeting{sessions.length === 1 ? "" : "s"}
        </span>
      </div>
      <ul>
        {sessions.slice(0, SHOWN).map((s) => (
          <li
            key={`${s.class_id}-${s.occurrence_id || s.date}`}
            className="flex justify-between gap-3 border-t border-[var(--studio-divider)] px-5 py-2.5 font-sans text-[14px]"
            data-testid="instructor-notes-owed-row"
          >
            <span className="min-w-0 truncate text-[var(--text-primary)]">{s.class_name}</span>
            <span className="shrink-0 text-[var(--text-tertiary)]">{dayLabel(s.date, { year: true })}</span>
          </li>
        ))}
      </ul>
      {more > 0 && <p className="px-5 pb-2 font-sans text-[13px] text-[var(--text-tertiary)]">and {more} more</p>}
      {owed?.complete === false && (
        <p className="px-5 pb-2 font-sans text-[12px] text-[var(--text-tertiary)]">
          Classes booked in Kalver Scheduler can&rsquo;t be checked, so this may be short.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 border-t border-[var(--studio-divider)] px-5 py-4">
        <Button variant="ghost" icon="mail" disabled={chasing} onPress={chase} data-testid="instructor-notes-chase">
          {chasing ? "Asking…" : "Chase notes"}
        </Button>
        <span className="font-sans text-[13px] text-[var(--text-tertiary)]">Lands in their updates in Hans. No email.</span>
      </div>
    </Card>
  );
}
